'use client';

/**
 * Popup de feedback centree, affichee juste apres une reponse a un QCM.
 *
 * Recompense « gamy » partagee par toutes les surfaces interactives : player V2
 * eleve, warmup, demo guidee (apps/app) et defi (apps/marketing). Message au
 * centre de l'ecran, XP gagnes mis en avant, bonus de serie quand l'eleve
 * enchaine les bonnes reponses. Purement motivationnelle : elle se ferme d'un
 * tap n'importe ou (fond OU carte). Le `onClose` est l'endroit ou l'appelant
 * decide de la suite (avancer, reveler un CTA, etc.).
 *
 * En cas de MAUVAISE reponse, on affiche en plus (si fournis) l'erreur commise
 * (`errorFeedbackMd`) et l'explication (`explanationMd`) — rendus en Markdown +
 * LaTeX via MarkdownMath. La carte devient scrollable si le contenu deborde.
 *
 * Mobile-first : carte pleine largeur (max-w-xs), bouton « Continuer »
 * `w-full` >= 44 px, `pb-safe`.
 */

import { useState, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { MarkdownMath } from '../content/markdown-math';
import type { XpReward } from './xp-rewards';
import { streakHeadline } from './xp-rewards';

/** Nombre max de flammes dessinees (la serie peut depasser visuellement). */
const MAX_FLAMES = 5;

export interface FeedbackPopupProps {
  reward: XpReward;
  wasCorrect: boolean;
  /** XP cumule de la session (affiche en pied de carte). */
  sessionXp: number;
  onClose: () => void;
  /** Libelle du bouton (defaut « Continuer »). */
  continueLabel?: string;
  /** Explication (Markdown+LaTeX), affichee uniquement sur mauvaise reponse. */
  explanationMd?: string | null;
  /** Feedback de l'erreur commise (Markdown+LaTeX), si l'auteur l'a renseigne. */
  errorFeedbackMd?: string | null;
  /**
   * Donner le focus au bouton « Continuer » a l'ouverture (defaut `true`).
   * A desactiver pour les surfaces en lecture automatique (showcase marketing) :
   * un autofocus a chaque etape volerait le focus et ferait sauter le scroll de
   * la page d'atterrissage.
   */
  autoFocusContinue?: boolean;
  /**
   * Classes additionnelles sur le fond (backdrop). Permet d'adapter l'overlay a
   * un cadre non plein-ecran : le showcase marketing le rend DANS un telephone
   * (coins arrondis + voile plus leger). Tailwind-merge laisse la derniere classe
   * gagner → on peut surcharger `bg-black/50` et ajouter un arrondi.
   */
  overlayClassName?: string;
  /**
   * Contenu additionnel rendu dans le corps, sous le detail XP (ex. le classement
   * de l'eleve sur la question + son rang general, cote Live School). Optionnel.
   */
  extra?: ReactNode;
  /** Force le titre (sinon derive de wasCorrect/serie). Ex. « Indices trouves ! ». */
  titleOverride?: string;
  /** Force l'emoji d'en-tete (sinon derive de wasCorrect). */
  emojiOverride?: string;
}

export function FeedbackPopup({
  reward,
  wasCorrect,
  sessionXp,
  onClose,
  continueLabel = 'Continuer',
  explanationMd,
  errorFeedbackMd,
  autoFocusContinue = true,
  overlayClassName,
  extra,
  titleOverride,
  emojiOverride,
}: FeedbackPopupProps) {
  const headline =
    titleOverride ?? (wasCorrect ? (streakHeadline(reward.streak) ?? 'Bravo !') : 'Pas grave !');
  const emoji = emojiOverride ?? (wasCorrect ? (reward.hasStreakBonus ? '🔥' : '🎉') : '💪');
  const flames = Math.min(reward.streak, MAX_FLAMES);
  // Sur mauvaise reponse, a-t-on de quoi expliquer ? (erreur et/ou explication)
  const hasLearnContent = !wasCorrect && !!(errorFeedbackMd?.trim() || explanationMd?.trim());
  // L'explication est repliee par defaut → popup compacte. Elle ne se deplie
  // qu'au clic sur « Explication », dans une zone defilante a hauteur bornee.
  const [showExplanation, setShowExplanation] = useState(false);

  return (
    <div
      className={cn(
        'animate-fade-in z-70 fixed inset-0 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm',
        overlayClassName,
      )}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={wasCorrect ? 'Bonne réponse' : 'Réponse incorrecte'}
    >
      {/* Pas de stopPropagation : un tap n'importe où (fond OU carte) ferme. */}
      <div
        className={cn(
          'pb-safe flex max-h-[88vh] w-full max-w-xs flex-col overflow-hidden rounded-3xl bg-white text-center shadow-2xl sm:max-w-md',
          wasCorrect ? 'animate-bounce-in' : 'animate-shake',
        )}
      >
        {/* En-tête coloré */}
        <div
          className={cn(
            'flex shrink-0 flex-col items-center gap-1 px-6 pb-5 pt-7',
            wasCorrect
              ? 'from-xp-500 to-xp-600 bg-linear-to-b text-white'
              : 'bg-error-50 text-error-600',
          )}
        >
          <span className="text-5xl leading-none" aria-hidden="true">
            {emoji}
          </span>
          <p className="font-display text-xl font-extrabold">{headline}</p>

          {/* Flammes de série (bonnes réponses) */}
          {wasCorrect && reward.streak >= 2 && (
            <div className="mt-1 flex items-center gap-0.5" aria-hidden="true">
              {Array.from({ length: flames }).map((_, i) => (
                <span key={i} className="text-base">
                  🔥
                </span>
              ))}
            </div>
          )}
          {wasCorrect && reward.streak >= 2 && (
            <p className="font-sans text-xs font-medium opacity-90">
              {reward.streak} bonnes réponses d’affilée
            </p>
          )}
        </div>

        {/* Corps scrollable : XP + (sur erreur) explication */}
        <div className="overflow-y-auto px-6 py-5">
          {/* XP gagnés — mis en avant */}
          <p
            className={cn(
              'font-display font-extrabold leading-none',
              wasCorrect ? 'text-xp-600 text-5xl' : 'text-ink-300 text-4xl',
            )}
          >
            {reward.total > 0 ? `+${reward.total}` : '+0'}
            <span className="ml-1 align-middle text-lg font-bold">XP</span>
          </p>

          {/* Détail base + bonus (série et/ou vitesse) */}
          {wasCorrect && (reward.streakBonus > 0 || reward.timeBonus > 0) && (
            <div className="text-ink-500 mt-2 flex flex-wrap items-center justify-center gap-2 font-sans text-xs">
              <span className="bg-ink-50 rounded-full px-2 py-0.5">base +{reward.base}</span>
              {reward.streakBonus > 0 && (
                <span className="bg-accent-100 text-accent-700 rounded-full px-2 py-0.5 font-semibold">
                  🔥 série +{reward.streakBonus}
                </span>
              )}
              {reward.timeBonus > 0 && (
                <span className="bg-xp-100 text-xp-700 rounded-full px-2 py-0.5 font-semibold">
                  ⚡ vitesse +{reward.timeBonus}
                </span>
              )}
            </div>
          )}

          {/* Mauvaise réponse : explication repliée derrière un bouton pour
              garder la popup compacte. Dépliée → zone défilante (max-h bornée). */}
          {hasLearnContent ? (
            <div className="mt-3">
              <button
                type="button"
                // stopPropagation : sans ça, le clic remonte au fond
                // (onClick={onClose}) et ferme la popup au lieu de déplier.
                onClick={(e) => {
                  e.stopPropagation();
                  setShowExplanation((v) => !v);
                }}
                aria-expanded={showExplanation}
                className="border-ink-200 text-ink-700 hover:bg-ink-50 mx-auto inline-flex min-h-9 items-center gap-1.5 rounded-full border px-4 py-1.5 font-sans text-sm font-semibold transition-colors"
              >
                Explication
                <span
                  aria-hidden="true"
                  className={cn('transition-transform', showExplanation && 'rotate-90')}
                >
                  →
                </span>
              </button>

              {showExplanation && (
                <div
                  // Le tap dans l'explication ne doit pas fermer la popup (seuls
                  // le fond et « Continuer » ferment). Zone défilante bornée.
                  onClick={(e) => e.stopPropagation()}
                  className="mt-3 max-h-[40vh] space-y-2 overflow-y-auto text-left"
                >
                  {errorFeedbackMd?.trim() && (
                    <div className="bg-error-50 border-error-100 text-ink-700 rounded-xl border px-3 py-2 text-sm">
                      <p className="text-error-600 mb-1 text-[10px] font-semibold uppercase tracking-wide">
                        Ton erreur
                      </p>
                      <MarkdownMath>{errorFeedbackMd}</MarkdownMath>
                    </div>
                  )}
                  {explanationMd?.trim() && (
                    <div className="bg-ink-50 border-ink-100 text-ink-700 rounded-xl border px-3 py-2 text-sm">
                      <p className="text-ink-400 mb-1 text-[10px] font-semibold uppercase tracking-wide">
                        Explication
                      </p>
                      <MarkdownMath>{explanationMd}</MarkdownMath>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            !wasCorrect && (
              <p className="text-ink-400 mt-2 font-sans text-xs">
                Continue, la prochaine est pour toi.
              </p>
            )
          )}

          {/* Contenu additionnel (ex. classement Live School). */}
          {extra}

          {/* Total session */}
          <p className="text-ink-400 mt-4 font-sans text-[11px] uppercase tracking-wider">
            Total : <span className="text-ink-600 font-bold">{sessionXp} XP</span>
          </p>

          <button
            type="button"
            // stopPropagation : sans ça, le clic sur ce bouton (w-full, occupe
            // toute la carte) déclenche `onClose` deux fois — une fois ici, une
            // fois en remontant jusqu'au fond (`onClick={onClose}` du conteneur).
            // Dans le player V2, `onClose` avance d'une étape → un double-close
            // sautait une étape (bug « double suivant »). Le fond et la carte
            // (sans bouton) gardent leur tap-pour-fermer.
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            autoFocus={autoFocusContinue}
            className="bg-accent-400 font-display hover:bg-accent-500 mt-4 min-h-11 w-full rounded-2xl px-6 py-2.5 text-sm font-bold text-[#0a2e57] shadow-md transition-all active:scale-[.98]"
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
